<template>
  <div class="container py-3">
    <!-- Page Header -->
    <div class="page-header">
      <h2>{{ $t('warehouse.title') }}</h2>
      <button class="btn btn-primary" @click="openAddModal">{{ $t('warehouse.add') }}</button>
    </div>

  <!-- Search -->
  <div class="search-wrapper mb-3">
    <span class="search-icon">🔍</span>
    <input v-model="search" type="text" class="form-control" placeholder="Search warehouses..." />
  </div>

  <!-- Table -->
  <div class="table-card">
    <table class="premium-table table">
      <thead>
        <tr>
          <th>{{ $t('common.name') }}</th>
          <th>{{ $t('common.location') }}</th>
          <th>{{ $t('common.actions') }}</th>
        </tr>
      </thead>

      <tbody>
        <tr v-if="warehouses.length === 0">
          <td colspan="3"><div class="empty-state"><div class="empty-icon">🏪</div><p>No warehouses found</p></div></td>
        </tr>
        <tr v-for="wh in warehouses" :key="wh.id">
          <td><strong>{{ wh.name }}</strong></td>
          <td>{{ wh.location }}</td>
          <td>
            <button class="btn-action btn-action-warning" @click="editWarehouse(wh)">✏️ {{ $t('common.edit') }}</button>
            <button class="btn-action btn-action-danger" @click="deleteWarehouse(wh.id)">🗑️ {{ $t('common.delete') }}</button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <!-- Pagination -->
  <MainPagination
    v-model:currentPage="currentPage"
    v-model:pageSize="pageSize"
    :total="totalRecords"
  />

  <!-- Add/Edit Modal -->
  <div v-if="showModal" class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditing ? $t('warehouse.edit') : $t('warehouse.add') }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveWarehouse">
            <div class="mb-3">
              <label class="form-label">{{ $t('common.name') }}</label>
              <input v-model="form.name" type="text" class="form-control" required />
            </div>

            <div class="mb-3">
              <label class="form-label">{{ $t('common.location') }}</label>
              <input v-model="form.location" type="text" class="form-control" />
            </div>

            <button type="submit" class="btn btn-primary w-100">
              {{ isEditing ? $t('common.update') : $t('common.add') }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import api from '../api/axios'
import { useI18n } from 'vue-i18n'
import MainPagination from '../components/MainPagination.vue'

const { t } = useI18n()


const warehouses = ref([])
const search = ref('')
const currentPage = ref(1)
const pageSize = ref(10)
const totalRecords = ref(0)
const showModal = ref(false)
const isEditing = ref(false)
const form = ref({ id: null, name: '', location: '' })

const fetchWarehouses = async () => {
  try {
    const res = await api.get('warehouses/', {
      params: {
        page: currentPage.value,
        page_size: pageSize.value,
        search: search.value
      }
    })
    warehouses.value = res.data.results
    totalRecords.value = res.data.count
  } catch (err) {
    console.error(err)
  }
}

let searchTimeout = null
watch(search, () => {
  currentPage.value = 1
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    fetchWarehouses()
  }, 300)
})

watch([currentPage, pageSize], () => {
  fetchWarehouses()
})

const openAddModal = () => {
  form.value = { id: null, name: '', location: '' }
  isEditing.value = false
  showModal.value = true
}

const editWarehouse = (wh) => {
  form.value = { ...wh }
  isEditing.value = true
  showModal.value = true
}

const saveWarehouse = async () => {
  if (isEditing.value) {
    await api.put(`warehouses/${form.value.id}/`, form.value)
  } else {
    await api.post('warehouses/', form.value)
  }
  await fetchWarehouses()
  closeModal()
}

const deleteWarehouse = async (id) => {
  if (confirm(t('warehouse.confirmDelete'))) {
    await api.delete(`warehouses/${id}/`)
    await fetchWarehouses()
  }
}

const closeModal = () => {
  showModal.value = false
}

onMounted(fetchWarehouses)
</script>

<style scoped>
.modal {
  background-color: rgba(0, 0, 0, 0.5);
}
</style>
